import React, { useState, useEffect } from 'react';

import '../../css/components/TruckLoanForm.css';
import { formatTaiwanDateTime, Message } from '../../utils';

import { createTruckLoanApplication } from '../../service/api/TruckLoanApplication/apiTruckLoanApplication';

const TruckLoanApplicationForm = ({
    deliverOrder,
    vehicleInfo,
    userInfo,
    deliverInfo,        // 從 CreateStaffOrderModal 傳入的司機資訊
    deliverOrderInfo,   // 從 CreateStaffOrderModal 傳入的派車單時間資訊
    mode = 'standalone', // 'standalone' 獨立提交 | 'embedded' 嵌入模式只回傳資料
    initialData,        // 初始數據 (用於恢復之前的狀態)
    onDataChange,       // 當數據改變時回調 (用於保存狀態)
    onSubmit,
    onCancel
}) => {
    // 整合 userInfo 和 deliverInfo
    const effectiveUserInfo = userInfo || deliverInfo;

    const [formData, setFormData] = useState(initialData || {
        // 自動帶入不可修改的欄位
        purpose: '',
        vehicleId: vehicleInfo?.id || '',
        destination: '',
        passengerCount: 1,
        startAt: '',
        finishAt: '',
        endAt: '',
        totalDays: '',
        category: deliverOrderInfo?.category || '',
        department: '',
        applicantName: effectiveUserInfo?.name || '',
        applicantDate: new Date().toISOString().split('T')[0],
        phone: '',
        cellphone: effectiveUserInfo?.phone || '',
        unitSupervisor: '',
        preCheckStatus: '',
        deliverOrderId: deliverOrder?.id || '',
        deliverId: effectiveUserInfo?.id || '',
    });

    useEffect(() => {
        if (deliverOrder) {
            // 帶入派車單資訊
            setFormData(prev => ({
                ...prev,
                startAt: formatTaiwanDateTime(deliverOrder.usedAt),
                endAt: formatTaiwanDateTime(deliverOrder.returnAt),
                deliverOrderId: deliverOrder.id,
            }));
        }
        // 從 CreateStaffOrderModal 傳入的派車單時間資訊
        if (deliverOrderInfo) {
            setFormData(prev => ({
                ...prev,
                startAt: deliverOrderInfo.usedAt ? formatTaiwanDateTime(deliverOrderInfo.usedAt) : prev.startAt,
                finishAt: deliverOrderInfo.finishAt ? formatTaiwanDateTime(deliverOrderInfo.finishAt) : prev.finishAt,
                endAt: deliverOrderInfo.returnAt ? formatTaiwanDateTime(deliverOrderInfo.returnAt) : prev.endAt,
            }));
        }
        if (vehicleInfo) {
            setFormData(prev => ({
                ...prev,
                vehicleId: vehicleInfo.id,
            }));
        }
        if (userInfo) {
            setFormData(prev => ({
                ...prev,
                applicantName: userInfo.name,
                cellphone: userInfo.phone,
                deliverId: userInfo.id,
            }));
        }
        // 從 CreateStaffOrderModal 傳入的司機資訊
        if (deliverInfo) {
            setFormData(prev => ({
                ...prev,
                applicantName: deliverInfo.name || prev.applicantName,
                cellphone: deliverInfo.phone || prev.cellphone,
                deliverId: deliverInfo.id || prev.deliverId,
            }));
        }
    }, [deliverOrder, deliverOrderInfo, vehicleInfo, userInfo, deliverInfo]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedData = {
            ...formData,
            [name]: value,
        };
        setFormData(updatedData);
        // 保存每次改變的數據
        if (onDataChange) {
            onDataChange(updatedData);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 驗證必填欄位
        const requiredFields = [
            'purpose', 'destination', 'passengerCount', 'startAt', 'endAt', 'totalDays',
            'department', 'applicantName', 'cellphone', 'unitSupervisor'
        ];

        for (const field of requiredFields) {
            if (!formData[field]) {
                // 針對時間欄位給出更明確的提示
                if (field === 'startAt' || field === 'endAt') {
                    Message('請先在派車單填寫用車時間和結束時間', 'warning');
                    return;
                }
                Message(`請填寫${field}`, 'warning');
                return;
            }
        }

        // embedded 模式：只回傳資料，不直接呼叫 API
        if (mode === 'embedded') {
            if (onSubmit) onSubmit(formData);
            return;
        }

        // standalone 模式：直接呼叫 API
        try {
            const response = await createTruckLoanApplication(formData);
            if (response?.status === 200 && response.data?.status === 1) {
                Message('借車申請已送出，等待協理審核', 'success');
                if (onSubmit) onSubmit(response.data.result);
            } else {
                Message(response?.data?.message || '申請失敗', 'warning');
            }
        } catch (error) {
            Message('申請過程發生錯誤: ' + error.message, 'error');
        }
    };

    return (
        <div className="truck-loan-form-container">
            <div className="truck-loan-form-header">
                <h2>貨車車輛借用申請單</h2>
                <span className="form-number">編號：{deliverOrder?.id ? `TL-${deliverOrder.id}` : '待產生'}</span>
                {onCancel && (
                    <button type="button" className="btn-close-form" onClick={onCancel} title="關閉">
                        ✕
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="truck-loan-form">
                {/* 申請單位填寫區 */}
                <div className="form-section">
                    <h3 className="section-title">申請單位填寫</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>申請用途 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="purpose"
                                value={formData.purpose}
                                onChange={handleChange}
                                placeholder="請輸入申請用途"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>車輛種類</label>
                            <input
                                type="text"
                                value={`${vehicleInfo?.brand || ''} ${vehicleInfo?.model || ''}`}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>車牌</label>
                            <input
                                type="text"
                                value={vehicleInfo?.plate || deliverOrder?.plate || ''}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <label>行駛地點 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="destination"
                                value={formData.destination}
                                onChange={handleChange}
                                placeholder="請輸入行駛地點"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>搭乘人數 <span className="required">*</span></label>
                            <input
                                type="number"
                                name="passengerCount"
                                value={formData.passengerCount}
                                onChange={handleChange}
                                min="1"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>用車開始時間</label>
                            <input
                                type="datetime-local"
                                name="startAt"
                                value={formData.startAt}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>用車結束時間</label>
                            <input
                                type="datetime-local"
                                name="endAt"
                                value={formData.endAt}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>共計日數 <span className="required">*</span></label>
                            <input
                                type="number"
                                name="totalDays"
                                value={formData.totalDays}
                                onChange={handleChange}
                                placeholder="請輸入天數"
                                step="0.5"
                                min="0.5"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* 使用規定 - 可收折 */}
                <div className="form-section usage-rules">
                    <details>
                        <summary className="section-title clickable">使用規定 (點擊展開)</summary>
                        <ol>
                            <strong>
                                <li>使用單位之車輛駕駛，必須具備小型車駕照，且具有長5.3m、寬2m、高2m規格之箱型車駕駛經驗者為佳。</li>
                                <li>車輛借出期間，駕駛須遵守交通規則，借用單位並應善盡保管之責，若遇交通事故或車身受損，相關罰單或維修費用須由借用單位負擔。</li>
                                <li>公務車行駛地點以申請核准為限，使用人不得變更行程挪為私用；車輛用畢後，亦須詳填車輛返還記錄等資料。</li>
                                <li>借用單位應支付車輛借用期間所衍生之油費、停車費、過路費及臨時維修費，並維護車內清潔。</li>
                                <li>車輛油料、e-tag、停車等費用，由借用單位自行支付核銷。</li>
                                <li>公務車輛使用完畢應駛返存放地點，會同保管單位驗車、完成歸還手續。</li>
                            </strong>
                        </ol>
                    </details>
                </div>

                {/* 申請人資訊 */}
                <div className="form-section">
                    <h3 className="section-title">申請人資訊</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>申請單位 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                placeholder="請輸入申請單位"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>申請人(駕駛人)</label>
                            <input
                                type="text"
                                value={formData.applicantName}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                        <div className="form-group">
                            <label>申請日期</label>
                            <input
                                type="date"
                                value={formData.applicantDate}
                                disabled
                                className="readonly-field"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>聯絡電話(辦公室)</label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="選填"
                            />
                        </div>
                        <div className="form-group">
                            <label>聯絡電話(手機) <span className="required">*</span></label>
                            <input
                                type="text"
                                name="cellphone"
                                value={formData.cellphone}
                                onChange={handleChange}
                                placeholder="請輸入手機號碼"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>單位主管 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="unitSupervisor"
                                value={formData.unitSupervisor}
                                onChange={handleChange}
                                placeholder="請輸入單位主管姓名"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* 按鈕區 */}
                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={onCancel}>
                        取消
                    </button>
                    <button type="submit" className="btn-submit">
                        送出申請
                    </button>
                </div>
            </form>
        </div>
    );
};

export { TruckLoanApplicationForm };
